import { AfterViewInit, Component, Input, OnChanges, forwardRef, booleanAttribute, Output, EventEmitter, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CursorPosition } from '@shared/interfaces/CursorPosition.interface';

@Component({
  selector: 'app-so-text-area',
  templateUrl: './so-text-area.component.html',
  styleUrls: ['./so-text-area.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoTextAreaComponent),
      multi: true
    }
  ]
})
export class SoTextAreaComponent implements OnChanges, AfterViewInit {

  @ViewChild('textarea') textarea!: ElementRef<HTMLTextAreaElement>;
  @Output() positionChanged = new EventEmitter<CursorPosition>();
  @Output() valueChanged = new EventEmitter<string>();

  @Input() text: string = '';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() rows: string = '2';
  @Input() disabled: boolean = false;
  @Input() styles: string = '';
  @Input() name: string = '';
  @Input() areaStyles: string = '';
  @Input() paddingClasses: string = 'p-1';
  @Input() inputId?: string;
  @Input() applySanitize: boolean = false;
  @Input() maxlength: number | null = 2000;
  @Input() labelStyle: string = '';
  @Input() formGroup?: any;
  @Input() activeBracesAndBracketsRestriction: boolean = false;
  @Input({ transform: booleanAttribute }) requiredMark: boolean = false;
  @Input({ transform: booleanAttribute }) autoResize: boolean = false;
  @Input() onlyAlphanumeric: boolean = false;
  @Input() formControl: FormControl | null = null;
  @Input() insertText: CursorPosition = {
    start: 0,
    end: 0
  };
  onChange: any = () => {};
  onTouch: any = () => {};

  writeValue(value: any): void {
    if (value !== undefined) {
      this.text = value;
      queueMicrotask(() => this.syncTextareaHeight());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text'] || changes['autoResize']) {
      queueMicrotask(() => this.syncTextareaHeight());
    }
  }

  ngAfterViewInit(): void {
    this.syncTextareaHeight();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  updateValue(value: string): void {
    this.text = value;
    this.onChange(value);
    this.onTouch();
    this.valueChanged.emit(value);
    this.syncTextareaHeight();
  }
  onCursorPositionChange(): void {
    if (this.textarea) {
      const textarea = this.textarea.nativeElement;
      this.positionChanged.emit({
        start: textarea.selectionStart,
        end: textarea.selectionEnd
      });
    }
  }
  onKeyDown(event: KeyboardEvent): void {
    if(this.activeBracesAndBracketsRestriction) this.handleBracesAndBrackets(event);
  }
  handleBracesAndBrackets(event: KeyboardEvent): void {
    const forbiddenKeys = ['{', '}', '[', ']'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault(); // bloquea la tecla
    }
  }
  onPaste(event: ClipboardEvent): void {
    if(this.activeBracesAndBracketsRestriction) this.blockPasteWithBracesAndBrackets(event);
  }
  blockPasteWithBracesAndBrackets(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const forbiddenPattern = /[{}\[\]]/;

    if (forbiddenPattern.test(pastedText)) {
      event.preventDefault(); // bloquea el pegado si contiene los caracteres
    }
  }

  onKeyPress(event: KeyboardEvent) {
    if (this.onlyAlphanumeric) {
      const regex = /^[a-zA-Z0-9\s.,]$/;
    
      if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
        event.preventDefault();
      }
    }
  }

  private syncTextareaHeight(): void {
    if (!this.textarea) return;

    const textarea = this.textarea.nativeElement;

    if (!this.autoResize) {
      textarea.style.height = '';
      textarea.style.overflowY = '';
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
}
